import { banStatusStrings, summaryManagementStrings } from 'constants/strings'
import { ELineChannelStatus, ELineWebhookStatus, EPausedBy } from 'types'
import type {
  TBanStatusChannel,
  TBanStatusChannelCategory,
  TBanStatusCounts,
  TBanStatusOrganization,
} from 'types'
import { formatNumber } from 'utils'

/**
 * チャンネルを画面上の「状態」1値へ写す（表示用のバッジ・停止理由）。
 * 絞り込みはサーバ側で行うため、ここでは表示だけを担う。
 *
 * status=PAUSED のときだけ pausedBy を見る（それ以外は参照してはいけない値のため）。
 * 停止していないのに一覧に出るのは払い出し条件を満たさないチャンネルで、
 * Webhookが異常なら webhookInvalid、それ以外は notAllocatable として扱う。
 */
export const getChannelCategory = (
  channel: TBanStatusChannel,
): TBanStatusChannelCategory => {
  if (channel.status === ELineChannelStatus.UNLINKED) return 'unlinked'
  if (channel.status === ELineChannelStatus.PAUSED) {
    return channel.pausedBy === EPausedBy.HEALTH_CHECK
      ? 'autoPaused'
      : 'manualPaused'
  }
  return channel.webhookStatus === ELineWebhookStatus.INVALID
    ? 'webhookInvalid'
    : 'notAllocatable'
}

export const getCategoryLabel = (category: TBanStatusChannelCategory): string =>
  banStatusStrings.categoryLabels[category]

/** 既存の詳細・編集APIが扱えるのは、所属組織の連携解除されていないチャンネルだけ。 */
export const canEditChannel = (
  channel: TBanStatusChannel,
  currentOrganizationId: number | undefined,
): boolean =>
  currentOrganizationId !== undefined &&
  channel.organizationId === currentOrganizationId &&
  channel.status !== ELineChannelStatus.UNLINKED

/**
 * 稼働させているアカウントのうち何割が止まっているか。
 *
 * UNLINKED（運用対象から外したもの）は分子だけでなく母数からも除く。
 * 母数に残すと、連携解除が多い組織ほど停止率が低く出てしまう。
 */
export const formatPausedRate = (counts: TBanStatusCounts): string => {
  const operating = counts.total - counts.unlinked
  if (operating <= 0) return summaryManagementStrings.unavailableMetric
  const paused = counts.autoPaused + counts.manualPaused
  return `${((paused / operating) * 100).toFixed(1)}%`
}

/** 友だち数の表示。上限が未設定なら分母を出さない。 */
export const formatFriends = (
  activeFriends: number,
  friendLimit: number | null,
): string =>
  friendLimit === null
    ? formatNumber(activeFriends)
    : `${formatNumber(activeFriends)} / ${formatNumber(friendLimit)}`

/** APIの未反映・欠損値を実測の0件として扱わない。 */
export const isAvailableBanStatusCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

export const formatBanStatusCount = (value: unknown): string =>
  isAvailableBanStatusCount(value)
    ? formatNumber(value)
    : summaryManagementStrings.unavailableMetric

/**
 * 連携解除を除くチャンネルを持つのに、払い出し可能が0件か。
 *
 * UNLINKED を母数に残すと、意図的に全アカウントを解除した組織が
 * 永久に「要対応」として赤く出続けるため除く（formatPausedRate と同じ扱い）。
 * バックエンドの isOrganizationBlocked と同じ定義。
 */
export const isOrganizationBlocked = (counts: TBanStatusCounts): boolean =>
  counts.total - counts.unlinked > 0 && counts.allocatable === 0

export type OrganizationSortKey = 'organizationName' | Exclude<
  keyof TBanStatusCounts,
  'activeFriends' | 'pausedFriends' | 'operatingUsers' | 'pausedUsers'
>
export type OrganizationSortDirection = 'asc' | 'desc'
export type OrganizationSort = {
  key: OrganizationSortKey
  direction: OrganizationSortDirection
} | null

export const ORGANIZATION_USER_COUNT_KEYS = [
  'totalUsers', 'blockedUsers', 'pausedOnlyUsers',
] as const satisfies readonly OrganizationSortKey[]

export const ORGANIZATION_TABLE_SORT_KEYS = [
  'organizationName', 'total', 'active', 'full', 'autoPaused', 'manualPaused',
  'unlinked', 'webhookInvalid', 'notAllocatable', 'allocatable',
  ...ORGANIZATION_USER_COUNT_KEYS,
] as const satisfies readonly OrganizationSortKey[]

export const ORGANIZATION_CARD_SORT_KEYS = [
  'organizationName', 'total', 'allocatable', 'autoPaused', 'manualPaused',
  'unlinked', 'notAllocatable', ...ORGANIZATION_USER_COUNT_KEYS, 'webhookInvalid',
] as const satisfies readonly OrganizationSortKey[]

const organizationSortLabels = {
  ...banStatusStrings.organizations.headers,
  ...banStatusStrings.categoryLabels,
  organizationName: banStatusStrings.organizations.headers.organization,
}

export const getOrganizationSortLabel = (key: OrganizationSortKey): string =>
  organizationSortLabels[key]

/** 新しい項目は名前なら昇順、件数なら降順。同じ項目の操作は順序を反転する。 */
export const nextOrganizationSort = (
  current: OrganizationSort,
  key: OrganizationSortKey,
): NonNullable<OrganizationSort> => ({
  key,
  direction: current?.key === key
    ? current.direction === 'asc' ? 'desc' : 'asc'
    : key === 'organizationName' ? 'asc' : 'desc',
})

/** 集計キャッシュを変更せず、同値は方向に関係なく組織IDで順序を固定する。 */
export const sortOrganizations = (
  organizations: readonly TBanStatusOrganization[],
  sort: OrganizationSort = null,
): TBanStatusOrganization[] => [...organizations].sort((a, b) => {
  if (sort === null) {
    return Number(isOrganizationBlocked(b)) - Number(isOrganizationBlocked(a)) ||
      b.autoPaused - a.autoPaused || a.organizationId - b.organizationId
  }
  let difference: number
  if (sort.key === 'organizationName') {
    difference = a.organizationName.localeCompare(b.organizationName, 'ja')
  } else {
    const left = a[sort.key]
    const right = b[sort.key]
    const leftAvailable = isAvailableBanStatusCount(left)
    const rightAvailable = isAvailableBanStatusCount(right)
    // 未取得は昇順・降順のどちらでも末尾に置き、0件と混同しない。
    if (!leftAvailable || !rightAvailable) {
      return Number(rightAvailable) - Number(leftAvailable) || a.organizationId - b.organizationId
    }
    difference = left - right
  }
  return difference * (sort.direction === 'asc' ? 1 : -1) ||
    a.organizationId - b.organizationId
})
